// TurnierInfoPage.jsx — Öffentliche Turnier-Detailseite (kein Login)
import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import PublicNav from '../components/PublicNav';
import './TurnierInfoPage.css';

function formatDate(dateStr, dateText) {
  if (dateText) return dateText;
  if (!dateStr) return '—';
  return new Date(dateStr).toLocaleDateString('de-DE', {
    weekday: 'long', year: 'numeric', month: 'long', day: 'numeric'
  });
}

function formatDateShort(dateStr) {
  if (!dateStr) return '—';
  return new Date(dateStr).toLocaleDateString('de-DE', {
    day: '2-digit', month: '2-digit', year: 'numeric'
  });
}

function formatPrice(val) {
  if (val == null || val === '') return null;
  return parseFloat(val).toFixed(2).replace('.', ',') + ' €';
}

function CountdownBadge({ deadline }) {
  const [diff, setDiff] = useState(null);

  useEffect(() => {
    const calc = () => {
      const ms = new Date(deadline) - Date.now();
      setDiff(ms);
    };
    calc();
    const id = setInterval(calc, 60000);
    return () => clearInterval(id);
  }, [deadline]);

  if (diff == null) return null;
  if (diff <= 0) return <span className="tip-countdown tip-countdown--closed">Anmeldung geschlossen</span>;

  const days = Math.floor(diff / 86400000);
  const hours = Math.floor((diff % 86400000) / 3600000);

  if (days > 30) return <span className="tip-countdown tip-countdown--open">Anmeldung offen</span>;
  if (days > 0) return <span className="tip-countdown tip-countdown--soon">Noch {days} Tag{days !== 1 ? 'e' : ''}</span>;
  return <span className="tip-countdown tip-countdown--urgent">Noch {hours} Stunde{hours !== 1 ? 'n' : ''}</span>;
}

function RegistrationStatusBox({ turnier }) {
  const deadline = turnier.anmeldeschluss ? new Date(turnier.anmeldeschluss) : null;
  const isOpen = deadline ? deadline > new Date() : false;
  const isRestricted = turnier.anmeldung_beschraenkung === 'nur_spezifische';

  return (
    <div className={`tip-reg-status${isOpen ? ' tip-reg-status--open' : ' tip-reg-status--closed'}`}>
      <div className="tip-reg-status-icon">{isOpen ? '✅' : '🔒'}</div>
      <div>
        <div className="tip-reg-status-title">
          {isOpen ? 'Anmeldung ist offen' : 'Anmeldung geschlossen'}
        </div>
        {isRestricted && (
          <div className="tip-reg-status-sub">Nur für zugelassene Vereine</div>
        )}
        {deadline && (
          <div className="tip-reg-status-sub">
            Anmeldeschluss: {formatDate(turnier.anmeldeschluss)}
          </div>
        )}
      </div>
    </div>
  );
}

function HowToRegister({ onAnmelden }) {
  return (
    <div className="tip-howto">
      <h3 className="tip-howto-title">So funktioniert die Anmeldung</h3>

      <div className="tip-howto-paths">
        <div className="tip-howto-path">
          <div className="tip-howto-path-title">🥋 Als Einzelsportler</div>
          <div className="tip-howto-steps">
            <div className="tip-howto-step">
              <div className="tip-step-num">1</div>
              <div className="tip-step-text">
                <strong>Registrieren</strong>
                <p>Erstelle deinen persönlichen Athleten-Account unter „Registrieren" (oben rechts).</p>
              </div>
            </div>
            <div className="tip-howto-step">
              <div className="tip-step-num">2</div>
              <div className="tip-step-text">
                <strong>Einloggen & Turnier wählen</strong>
                <p>Logge dich ein, öffne das gewünschte Turnier und klicke „Anmelden".</p>
              </div>
            </div>
            <div className="tip-howto-step">
              <div className="tip-step-num">3</div>
              <div className="tip-step-text">
                <strong>Bestätigung</strong>
                <p>Du erhältst eine Bestätigung. Die genaue Kategorie wird bei der Einwaage am Turniertag festgelegt.</p>
              </div>
            </div>
          </div>
        </div>

        <div className="tip-howto-path">
          <div className="tip-howto-path-title">🏢 Als Verein</div>
          <div className="tip-howto-steps">
            <div className="tip-howto-step">
              <div className="tip-step-num">1</div>
              <div className="tip-step-text">
                <strong>Vereins-Login</strong>
                <p>Dein Verein benötigt einen Zugang. Wende dich an den TDA-Support falls noch keiner vorhanden.</p>
              </div>
            </div>
            <div className="tip-howto-step">
              <div className="tip-step-num">2</div>
              <div className="tip-step-text">
                <strong>Wettkämpfer & Kategorien</strong>
                <p>Wähle eure Wettkämpfer und ordne sie den Kategorien zu (Gewichts- und Altersklasse).</p>
              </div>
            </div>
            <div className="tip-howto-step">
              <div className="tip-step-num">3</div>
              <div className="tip-step-text">
                <strong>Bestätigung & Zahlung</strong>
                <p>Prüfe die Anmeldung und schließe die Zahlung ab. Alle angemeldeten Sportler erhalten eine E-Mail.</p>
              </div>
            </div>
          </div>
        </div>
      </div>

      <div className="tip-howto-cta">
        <button className="tip-btn-primary" onClick={onAnmelden}>
          Jetzt anmelden →
        </button>
        <a className="tip-btn-ghost" href="mailto:info@tda-intl.com">
          Fragen? Kontakt aufnehmen
        </a>
      </div>
    </div>
  );
}

// Kategorien nach Typ gruppieren
function groupKategorien(kategorien) {
  const groups = {
    'Kumite': [], 'Kata': [], 'Formen': [], 'Bruchtest': [],
    'Team': [], 'Sonstige': []
  };
  for (const k of kategorien) {
    const lower = k.toLowerCase();
    if (lower.includes('kumite')) groups['Kumite'].push(k);
    else if (lower.includes('kata')) groups['Kata'].push(k);
    else if (lower.includes('form')) groups['Formen'].push(k);
    else if (lower.includes('bruch')) groups['Bruchtest'].push(k);
    else if (lower.includes('team')) groups['Team'].push(k);
    else groups['Sonstige'].push(k);
  }
  return Object.entries(groups).filter(([, items]) => items.length > 0);
}

const TABS = [
  { id: 'info',       label: '📋 Überblick' },
  { id: 'kategorien', label: '🥋 Kategorien' },
  { id: 'preise',     label: '💰 Preise & Anmeldung' },
  { id: 'ablauf',     label: '📖 Ablauf & Info' },
];

export default function TurnierInfoPage() {
  const { id } = useParams();
  const navigate = useNavigate();
  const [turnier, setTurnier] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [tab, setTab] = useState('info');

  const handleAnmelden = () => {
    const athleteToken = localStorage.getItem('athleteToken');
    const clubToken    = localStorage.getItem('token');
    if (athleteToken) navigate('/athlete/dashboard');
    else if (clubToken) navigate(`/registration?turnierId=${id}`);
    else navigate('/login');
  };

  useEffect(() => {
    fetch(`/api/turniere/public/${id}/info`)
      .then(r => r.json())
      .then(d => {
        if (d.success) setTurnier(d.data);
        else setError('Turnier nicht gefunden.');
      })
      .catch(() => setError('Verbindungsfehler.'))
      .finally(() => setLoading(false));
  }, [id]);

  if (loading) return (
    <div className="tip-page">
      <PublicNav />
      <div className="tip-loading"><div className="tip-spinner" /><span>Lädt…</span></div>
    </div>
  );

  if (error || !turnier) return (
    <div className="tip-page">
      <PublicNav />
      <div className="tip-error">⚠ {error || 'Turnier nicht gefunden.'}</div>
    </div>
  );

  const kategorienGrouped = groupKategorien(turnier.kategorien ?? []);
  const hasFruehbucher = turnier.fruehbucher_bis && new Date(turnier.fruehbucher_bis) > new Date();
  const fruehbucherActive = hasFruehbucher && turnier.fruehbucher_rabatt > 0;

  return (
    <div className="tip-page">
      <PublicNav />

      {/* ── Banner (falls vorhanden) ── */}
      {turnier.banner_url && (
        <div className="tip-banner">
          <img src={turnier.banner_url} alt={`${turnier.name} Banner`} className="tip-banner-img" />
        </div>
      )}

      {/* ── Hero mit Plakat ── */}
      <div className={`tip-hero${turnier.banner_url ? ' tip-hero--has-banner' : ''}`}>
        {turnier.plakat_url ? (
          <img src={turnier.plakat_url} alt={turnier.name} className="tip-hero-poster" />
        ) : (
          <div className="tip-hero-placeholder">
            <div className="tip-hero-placeholder-icon">🏆</div>
          </div>
        )}
        <div className="tip-hero-overlay">
          <div className="tip-hero-content">
            {turnier.disziplin && <div className="tip-hero-discipline">{turnier.disziplin}</div>}
            <h1 className="tip-hero-name">{turnier.name}</h1>
            <div className="tip-hero-meta">
              <span className="tip-hero-date">📅 {formatDate(turnier.datum, turnier.datum_text)}</span>
              <span className="tip-hero-loc">📍 {turnier.ort}</span>
            </div>
            {turnier.anmeldeschluss && <CountdownBadge deadline={turnier.anmeldeschluss} />}
          </div>
        </div>
      </div>

      {/* ── Tab-Navigation ── */}
      <div className="tip-tabs-wrap">
        <div className="tip-tabs">
          {TABS.map(t => (
            <button
              key={t.id}
              className={`tip-tab${tab === t.id ? ' tip-tab--active' : ''}`}
              onClick={() => setTab(t.id)}
            >
              {t.label}
            </button>
          ))}
        </div>
      </div>

      {/* ── Tab-Inhalt ── */}
      <div className="tip-content">

        {/* ━━ TAB 1: ÜBERBLICK ━━ */}
        {tab === 'info' && (
          <div className="tip-tab-panel">
            <div className="tip-overview-grid">

              {/* Wichtige Fakten */}
              <div className="tip-card">
                <h2 className="tip-card-title">Auf einen Blick</h2>
                <dl className="tip-dl">
                  <dt>Datum</dt>
                  <dd>{formatDate(turnier.datum, turnier.datum_text)}</dd>

                  <dt>Ort / Austragungsort</dt>
                  <dd>{turnier.ort}</dd>

                  {turnier.disziplin && (
                    <>
                      <dt>Disziplin</dt>
                      <dd>{turnier.disziplin}</dd>
                    </>
                  )}


                  <dt>Anmeldeschluss</dt>
                  <dd className="tip-dl-deadline">
                    {formatDate(turnier.anmeldeschluss)}
                    {turnier.anmeldeschluss && <CountdownBadge deadline={turnier.anmeldeschluss} />}
                  </dd>

                  {turnier.anmeldungen_count > 0 && (
                    <>
                      <dt>Bereits angemeldet</dt>
                      <dd>{turnier.anmeldungen_count} Wettkämpfer</dd>
                    </>
                  )}

                  {turnier.veranstalter && (
                    <>
                      <dt>Veranstalter</dt>
                      <dd>{turnier.veranstalter}</dd>
                    </>
                  )}

                  {turnier.kontakt_email && (
                    <>
                      <dt>Kontakt</dt>
                      <dd><a href={`mailto:${turnier.kontakt_email}`} className="tip-link">{turnier.kontakt_email}</a></dd>
                    </>
                  )}
                </dl>
              </div>

              {/* Anmeldestatus */}
              <div className="tip-card">
                <h2 className="tip-card-title">Anmeldestatus</h2>
                <RegistrationStatusBox turnier={turnier} />

                {fruehbucherActive && (
                  <div className="tip-fruehbucher-badge">
                    🎯 Frühbucher-Rabatt aktiv bis {formatDateShort(turnier.fruehbucher_bis)}
                  </div>
                )}

                {/* Kurzübersicht Preise */}
                {turnier.preis_einzel && (
                  <div className="tip-price-preview">
                    <span>Startgebühr ab</span>
                    <strong className="tip-price-highlight">
                      {fruehbucherActive
                        ? formatPrice(parseFloat(turnier.preis_einzel) - parseFloat(turnier.fruehbucher_rabatt || 0))
                        : formatPrice(turnier.preis_einzel)}
                    </strong>
                    {fruehbucherActive && (
                      <span className="tip-price-orig">{formatPrice(turnier.preis_einzel)}</span>
                    )}
                  </div>
                )}

                <button className="tip-btn-primary tip-btn-full" onClick={handleAnmelden}>
                  Jetzt anmelden →
                </button>
                <button className="tip-btn-ghost tip-btn-full" onClick={() => setTab('preise')}>
                  Preise & Details ansehen
                </button>
              </div>
            </div>

            {/* Anmeldeanleitung */}
            <HowToRegister onAnmelden={handleAnmelden} />
          </div>
        )}

        {/* ━━ TAB 2: KATEGORIEN ━━ */}
        {tab === 'kategorien' && (
          <div className="tip-tab-panel">
            {kategorienGrouped.length === 0 ? (
              <div className="tip-empty">Kategorien werden noch bekannt gegeben.</div>
            ) : (
              <>
                <div className="tip-kat-intro">
                  <p>Das Turnier bietet <strong>{turnier.kategorien?.length ?? 0} Kategorien</strong> in folgenden Disziplinen an. Jede Kategorie ist nach Altersklasse und — bei Kumite — Gewichtsklasse unterteilt.</p>
                </div>
                <div className="tip-kat-groups">
                  {kategorienGrouped.map(([group, items]) => (
                    <div key={group} className="tip-kat-group">
                      <h3 className="tip-kat-group-title">{group}</h3>
                      <div className="tip-kat-list">
                        {items.map((k, i) => (
                          <div key={i} className="tip-kat-item">{k}</div>
                        ))}
                      </div>
                    </div>
                  ))}
                </div>
                <div className="tip-kat-note">
                  <span>ℹ️</span>
                  <span>Nicht sicher welche Kategorie passt? Wende dich an deinen Trainer oder <a href="mailto:info@tda-intl.com" className="tip-link">kontaktiere uns</a>.</span>
                </div>
              </>
            )}
          </div>
        )}

        {/* ━━ TAB 3: PREISE ━━ */}
        {tab === 'preise' && (
          <div className="tip-tab-panel">
            <div className="tip-price-grid">
              <div className="tip-card">
                <h2 className="tip-card-title">Startgebühren</h2>
                <div className="tip-price-table">
                  {turnier.preis_einzel != null && (
                    <div className="tip-price-row">
                      <span>Erste Kategorie (Einzel)</span>
                      <strong>{formatPrice(turnier.preis_einzel)}</strong>
                    </div>
                  )}
                  {turnier.rabatt_zweite_kategorie != null && parseFloat(turnier.rabatt_zweite_kategorie) > 0 && (
                    <div className="tip-price-row tip-price-row--discount">
                      <span>Zweite Kategorie (Rabatt)</span>
                      <strong>
                        {formatPrice(parseFloat(turnier.preis_einzel || 0) - parseFloat(turnier.rabatt_zweite_kategorie))}
                        <span className="tip-price-saved"> (−{formatPrice(turnier.rabatt_zweite_kategorie)})</span>
                      </strong>
                    </div>
                  )}
                  {turnier.rabatt_weitere_kategorien != null && parseFloat(turnier.rabatt_weitere_kategorien) > 0 && (
                    <div className="tip-price-row tip-price-row--discount">
                      <span>Weitere Kategorien (Rabatt)</span>
                      <strong>
                        {formatPrice(parseFloat(turnier.preis_einzel || 0) - parseFloat(turnier.rabatt_weitere_kategorien))}
                        <span className="tip-price-saved"> (−{formatPrice(turnier.rabatt_weitere_kategorien)})</span>
                      </strong>
                    </div>
                  )}
                  {turnier.preis_team != null && (
                    <div className="tip-price-row">
                      <span>Team-Anmeldung (pro Team)</span>
                      <strong>{formatPrice(turnier.preis_team)}</strong>
                    </div>
                  )}
                </div>
              </div>

              {(turnier.fruehbucher_bis || turnier.fruehbucher_rabatt) && (
                <div className="tip-card tip-card--highlight">
                  <h2 className="tip-card-title">🎯 Frühbucher-Angebot</h2>
                  {turnier.fruehbucher_bis && (
                    <p className="tip-fruehbucher-deadline">
                      Gültig bis: <strong>{formatDate(turnier.fruehbucher_bis)}</strong>
                      {hasFruehbucher
                        ? <span className="tip-badge-active"> Noch aktiv</span>
                        : <span className="tip-badge-expired"> Abgelaufen</span>}
                    </p>
                  )}
                  {turnier.fruehbucher_rabatt && (
                    <div className="tip-fruehbucher-amount">
                      <span>Rabatt</span>
                      <strong>− {formatPrice(turnier.fruehbucher_rabatt)}</strong>
                    </div>
                  )}
                  {turnier.preis_einzel && turnier.fruehbucher_rabatt && (
                    <div className="tip-fruehbucher-result">
                      <span>Preis mit Frühbucher</span>
                      <strong className="tip-price-highlight">
                        {formatPrice(parseFloat(turnier.preis_einzel) - parseFloat(turnier.fruehbucher_rabatt))}
                      </strong>
                    </div>
                  )}
                </div>
              )}
            </div>

            <div className="tip-payment-info">
              <h2 className="tip-card-title">Zahlung & Buchung</h2>
              <div className="tip-payment-steps">
                <div className="tip-payment-step">
                  <div className="tip-payment-step-icon">🏦</div>
                  <div>
                    <strong>Überweisung</strong>
                    <p>Nach der Anmeldung erhältst du die Bankdaten per E-Mail. Die Startgebühr wird per Überweisung vor dem Turnier beglichen.</p>
                  </div>
                </div>
                <div className="tip-payment-step">
                  <div className="tip-payment-step-icon">💵</div>
                  <div>
                    <strong>Barzahlung vor Ort</strong>
                    <p>Barzahlung ist direkt bei der Einwaage am Turniertag möglich. Bitte den genauen Betrag mitbringen.</p>
                  </div>
                </div>
                <div className="tip-payment-step">
                  <div className="tip-payment-step-icon">📧</div>
                  <div>
                    <strong>Bestätigungs-E-Mail</strong>
                    <p>Nach erfolgreicher Anmeldung erhältst du eine Bestätigung per E-Mail mit allen Turnier-Details.</p>
                  </div>
                </div>
                <div className="tip-payment-step">
                  <div className="tip-payment-step-icon">↩️</div>
                  <div>
                    <strong>Stornierung</strong>
                    <p>Stornierungen sind bis zum Anmeldeschluss möglich. Bitte kontaktiere uns direkt bei Fragen zur Rückerstattung.</p>
                  </div>
                </div>
              </div>
            </div>

            <div className="tip-register-cta">
              <button className="tip-btn-primary tip-btn-large" onClick={() => navigate('/login')}>
                Jetzt anmelden →
              </button>
              <p className="tip-register-cta-note">
                Für Einzelsportler & Vereine · Bei Fragen: <a href="mailto:info@tda-intl.com" className="tip-link">info@tda-intl.com</a>
              </p>
            </div>
          </div>
        )}

        {/* ━━ TAB 4: ABLAUF & INFO ━━ */}
        {tab === 'ablauf' && (
          <div className="tip-tab-panel">

            {turnier.beschreibung ? (
              <div className="tip-beschreibung">
                <h2 className="tip-card-title">Beschreibung & Ablauf</h2>
                <div className="tip-beschreibung-text">
                  {turnier.beschreibung.split('\n').map((line, i) => (
                    line.trim() ? <p key={i}>{line}</p> : <br key={i} />
                  ))}
                </div>
              </div>
            ) : (
              <div className="tip-card">
                <h2 className="tip-card-title">Turnier-Ablauf (Allgemein)</h2>
                <div className="tip-ablauf-default">
                  <div className="tip-ablauf-step">
                    <div className="tip-ablauf-icon">⚖️</div>
                    <div>
                      <strong>Einwaage (Weigh-In)</strong>
                      <p>Am Turniertag findet vor Beginn die offizielle Einwaage statt. Alle Wettkämpfer werden in ihrer Gewichtsklasse erfasst. Bitte pünktlich erscheinen — die genaue Uhrzeit wird vor dem Turnier bekanntgegeben.</p>
                    </div>
                  </div>
                  <div className="tip-ablauf-step">
                    <div className="tip-ablauf-icon">📋</div>
                    <div>
                      <strong>Eröffnung & Auslosung</strong>
                      <p>Nach der Einwaage findet die offizielle Eröffnung statt. Die Brackets (Kampfpaarungen) werden automatisch generiert und sind live auf dem Display einsehbar.</p>
                    </div>
                  </div>
                  <div className="tip-ablauf-step">
                    <div className="tip-ablauf-icon">⚔️</div>
                    <div>
                      <strong>Wettkampf</strong>
                      <p>Die Kämpfe werden nach Kategorien auf den einzelnen Tatamis ausgetragen. Das Aufruf-Display zeigt an, wer als nächstes dran ist. Bleibt immer in der Nähe eurer Tatami.</p>
                    </div>
                  </div>
                  <div className="tip-ablauf-step">
                    <div className="tip-ablauf-icon">🏅</div>
                    <div>
                      <strong>Siegerehrung</strong>
                      <p>Nach Abschluss aller Kämpfe einer Kategorie findet die Siegerehrung statt. Ergebnisse sind live auf der TDA-Website einsehbar.</p>
                    </div>
                  </div>
                </div>
              </div>
            )}

            <div className="tip-checklist-card">
              <h2 className="tip-card-title">✅ Checkliste für Wettkämpfer</h2>
              <div className="tip-checklist">
                <div className="tip-check-item"><span className="tip-check-icon">👕</span> Wettkampfkleidung (Gi / Sportbekleidung)</div>
                <div className="tip-check-item"><span className="tip-check-icon">🦺</span> Schutzausrüstung (je nach Kategorie — siehe Regelwerk)</div>
                <div className="tip-check-item"><span className="tip-check-icon">💧</span> Getränke und Verpflegung für den Tag</div>
                <div className="tip-check-item"><span className="tip-check-icon">⏰</span> Pünktliches Erscheinen zur Einwaage</div>
              </div>
            </div>

            <div className="tip-contact-box">
              <div className="tip-contact-icon">💬</div>
              <div>
                <strong>Fragen zum Turnier?</strong>
                <p>Wir helfen gerne weiter. Schreib uns eine E-Mail oder wende dich direkt an deinen Vereinsverantwortlichen.</p>
                <a href="mailto:info@tda-intl.com" className="tip-btn-ghost tip-btn-inline">
                  info@tda-intl.com schreiben
                </a>
              </div>
            </div>
          </div>
        )}
      </div>

      <footer className="tip-footer">
        <div className="tip-footer-inner">
          <a href="/">← Zurück zu allen Veranstaltungen</a>
          <span>© {new Date().getFullYear()} TDA International</span>
        </div>
      </footer>
    </div>
  );
}
